import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { CategoriesWithRatingsByYearSegment } from '../../types/categories-with-ratings-by-year-segment';
import { Category, YearSegment } from '../../types/category';
import { RatingSentence } from '../../types/rating-sentence';
import YearSegmentDivider from './YearSegmentDivider';

interface CategoryListProps {
  categoriesWithRatingsByYearSegment: CategoriesWithRatingsByYearSegment;
  onCategoryEdit: (category: Category) => void;
  onCategoryDelete: (category: Category) => void;
  onRatingEdit: (ratin: RatingSentence) => void;
  onRatingDelete: (rating: RatingSentence) => void;
  showFeminine: boolean;
}

const CategoryList = ({
  categoriesWithRatingsByYearSegment,
  onCategoryEdit,
  onCategoryDelete,
  onRatingEdit,
  onRatingDelete,
  showFeminine,
}: CategoryListProps) => {
  const showWholeYearDivider = (): boolean =>
    categoriesWithRatingsByYearSegment.wholeYear.length > 0 &&
    (categoriesWithRatingsByYearSegment.firstHalf.length > 0 ||
      categoriesWithRatingsByYearSegment.secondHalf.length > 0);

  const renderCategoryList = (
    category: Category,
    ratings: RatingSentence[],
    index: number
  ) => (
    <Box key={category.id}>
      <Box display="flex" justifyContent={'space-between'} mt={2}>
        <Box display="flex" alignItems={'center'} pr={2} gap={1}>
          <Typography variant="h6" color={'gray'} sx={{ userSelect: 'none' }}>
            {index + 1}
          </Typography>
          <Box
            display="flex"
            gap={1}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
          >
            <Typography variant="h6" noWrap>
              {category.name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" pr={2} gap={1}>
          <IconButton onClick={() => onCategoryEdit(category)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => onCategoryDelete(category)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {ratings.map((rating) => (
              <TableRow key={rating.id} className="HoverRow">
                <TableCell sx={{ px: 2, py: 1.5 }}>
                  {showFeminine ? rating.feminine ?? rating.text : rating.text}
                </TableCell>
                <TableCell className="RowButtonCell" align="right">
                  <Box>
                    <Box width={16} />
                    <IconButton onClick={() => onRatingEdit(rating)}>
                      <EditIcon />
                    </IconButton>
                    <Box width={8} />
                    <IconButton onClick={() => onRatingDelete(rating)}>
                      <DeleteIcon />
                    </IconButton>
                    <Box width={16} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      {showWholeYearDivider() && (
        <YearSegmentDivider yearSegment={YearSegment.WholeYear} />
      )}
      {categoriesWithRatingsByYearSegment.wholeYear.map(
        ({ category, ratings }, index) =>
          renderCategoryList(category, ratings, index)
      )}
      {categoriesWithRatingsByYearSegment.firstHalf.length > 0 && (
        <>
          <YearSegmentDivider yearSegment={YearSegment.FirstHalf} />
          {categoriesWithRatingsByYearSegment.firstHalf.map(
            ({ category, ratings }, index) =>
              renderCategoryList(category, ratings, index)
          )}
        </>
      )}
      {categoriesWithRatingsByYearSegment.secondHalf.length > 0 && (
        <>
          <YearSegmentDivider yearSegment={YearSegment.SecondHalf} />
          {categoriesWithRatingsByYearSegment.secondHalf.map(
            ({ category, ratings }, index) =>
              renderCategoryList(category, ratings, index)
          )}
        </>
      )}
    </>
  );
};

export default CategoryList;
